import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AuthContext, type AuthContextValue } from './authContext';
import { INITIAL_AUTH_STATE, type AuthSource, type AuthState } from './types';

interface AuthProviderProps {
  source: AuthSource;
  children: ReactNode;
}

/**
 * Provee el estado de sesión al árbol. El `source` es inyectable: en producción
 * es el adaptador Firebase; en tests, un fake síncrono.
 */
export function AuthProvider({ source, children }: AuthProviderProps): ReactNode {
  const [state, setState] = useState<AuthState>(INITIAL_AUTH_STATE);

  useEffect(() => source.subscribe(setState), [source]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signInWithGoogle: () => source.signInWithGoogle(),
      signOut: () => source.signOut(),
      signInWithEmail: source.signInWithEmail?.bind(source),
      signInWithApple: source.signInWithApple?.bind(source),
      resetPassword: source.resetPassword?.bind(source),
      register: source.register?.bind(source),
      refresh: source.refresh?.bind(source),
    }),
    [state, source],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
